import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, Edit2, Trash2, Building2, Mail, Phone } from 'lucide-react';
import AgentFormModal from '@/components/AgentFormModal';

export default function AgentsPage() {
  const { api } = useAuth();
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/agents', { params: { search } });
      setAgents(Array.isArray(data) ? data : []);
    } catch { setAgents([]); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/agents/${id}`); setConfirmDelete(null); load(); } catch {/* surfaced by UI */}
  };

  // Group by company so listings look clean when one RE has many agents.
  const grouped = agents.reduce((acc, a) => {
    const key = a.company || '(No company)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  return (
    <div data-testid="agents-page" className="space-y-5">
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center ring-1 ring-[#D35400]/10">
              <Building2 size={22} className="text-[#D35400]" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Real Estate</p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Managing Agents</h1>
              <p className="text-sm text-slate-500 mt-1">{agents.length} agent{agents.length === 1 ? '' : 's'} across {Object.keys(grouped).length} compan{Object.keys(grouped).length === 1 ? 'y' : 'ies'}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="agents-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search agents / company…"
                className="pl-8 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm w-64 focus:outline-none focus:border-[#D35400]"
              />
            </div>
            <button data-testid="new-agent-btn" onClick={() => { setEditAgent(null); setShowModal(true); }} className="flex items-center gap-2 px-3.5 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000]">
              <Plus size={14} strokeWidth={2.5} /> New Agent
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-12 text-center">
          <Building2 size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No agents yet — add your first agent to start linking rental properties to managing companies.</p>
        </div>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([company, list]) => (
          <div key={company} className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-[#E2E2DF] flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">{company}</h2>
              <span className="text-[11px] font-mono-num text-slate-500">{list.length} agent{list.length === 1 ? '' : 's'}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-[#E2E2DF]">
                <tr className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  <th className="text-left px-5 py-2.5">Ref</th>
                  <th className="text-left px-5 py-2.5">Agent</th>
                  <th className="text-left px-5 py-2.5">Phone</th>
                  <th className="text-left px-5 py-2.5">Email</th>
                  <th className="text-left px-5 py-2.5">Notes</th>
                  <th className="text-right px-5 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(a => (
                  <tr key={a._id} data-testid={`agent-row-${a.agent_no}`} className="border-b border-[#F0EFEC] last:border-b-0 hover:bg-slate-50 group">
                    <td className="px-5 py-2.5"><span className="font-mono-num text-xs font-bold text-[#D35400]">{a.agent_no}</span></td>
                    <td className="px-5 py-2.5 font-medium text-slate-800">{a.name || '—'}{a.active === false && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">inactive</span>}</td>
                    <td className="px-5 py-2.5 text-slate-600"><span className="inline-flex items-center gap-1"><Phone size={12} className="text-slate-400" />{a.phone || '—'}</span></td>
                    <td className="px-5 py-2.5 text-slate-600"><span className="inline-flex items-center gap-1"><Mail size={12} className="text-slate-400" />{a.email || '—'}</span></td>
                    <td className="px-5 py-2.5 text-xs text-slate-500 max-w-xs truncate">{a.notes || '—'}</td>
                    <td className="px-5 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button data-testid={`edit-agent-${a.agent_no}`} onClick={() => { setEditAgent(a); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-[#D35400]" aria-label="Edit"><Edit2 size={14} /></button>
                        {confirmDelete === a._id ? (
                          <>
                            <button data-testid={`confirm-delete-agent-${a.agent_no}`} onClick={() => remove(a._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-xs">Delete</button>
                            <button onClick={() => setConfirmDelete(null)} className="px-2 py-0.5 border border-slate-300 rounded text-xs text-slate-500">Cancel</button>
                          </>
                        ) : (
                          <button data-testid={`delete-agent-${a.agent_no}`} onClick={() => setConfirmDelete(a._id)} className="p-1.5 text-slate-400 hover:text-red-500" aria-label="Delete"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <AgentFormModal
        open={showModal}
        agent={editAgent}
        onClose={() => { setShowModal(false); setEditAgent(null); }}
        onSaved={() => { setShowModal(false); setEditAgent(null); load(); }}
      />
    </div>
  );
}
